/* eslint-disable jsx-a11y/label-has-associated-control */
import { useCallback, useEffect, useState } from "react";
import {
  Textarea,
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
  CodeBlock,
} from "@altimateai/lego";
import { DatapilotCliMcpOutput, MCPIntegrationType } from "./constants";
import { CommonIntegrationInstance } from "./mcpSlice";

interface McpConfigImporterProps {
  onServerSelected: (server: Partial<CommonIntegrationInstance>) => void;
  isEditing?: boolean;
}

const McpConfigImporter = ({ onServerSelected, isEditing = false }: McpConfigImporterProps) => {
  const [jsonInput, setJsonInput] = useState("");
  const [jsonError, setJsonError] = useState<string | null>(null);

  const handleJsonSubmit = useCallback(() => {
    try {
      const parsed = JSON.parse(jsonInput);
      // Assume parsed is similar to details.tools
      const tools = Array.isArray(parsed.tools) ? parsed.tools : [];
      const instance: Partial<CommonIntegrationInstance> = {
        type: MCPIntegrationType.MCP,
        name: parsed.name,
        // @ts-expect-error valid
        command: parsed.command,
        config: parsed.config || [],
        tools: toolsToToolConfig(tools),
        arguments: parsed.args,
        env: parsed.env,
      };
      if (isEditing) {
        delete instance.name;
      }

      onServerSelected(instance);
    } catch (err) {
      setJsonError("Invalid JSON");
    }
  }, [jsonInput, onServerSelected, isEditing]);

  useEffect(() => {
    if (!jsonInput) return;
    handleJsonSubmit();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jsonInput]);

  const handleJsonChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setJsonInput(e.target.value);
    setJsonError(null);
  };

  const toolsToToolConfig = (
    tools: NonNullable<DatapilotCliMcpOutput["tools"]>[number][] | null | undefined
  ) => {
    if (!Array.isArray(tools)) return undefined;
    return tools.map(tool => {
      return {
        name: tool.name,
        key: tool.name,
        description: tool.description,
      };
    });
  };

  return (
    <div className="">
      <div className="al-space-y-2">
        <p>Follow these steps to import your MCP config:</p>
        <ul>
          {!isEditing && (
            <>
              <li>
                1. Install datapilot cli
                <CodeBlock
                  code="pip install altimate-datapilot-cli"
                  language="sql"
                  theme="dark"
                  editorTheme="tomorrow"
                />
              </li>
              <li>
                2. Run the following command and enter the mcp server details:
                <CodeBlock
                  className="al-mb-0"
                  code="datapilot mcp inspect-mcp-server"
                  language="sql"
                  theme="dark"
                  editorTheme="tomorrow"
                />
                <Accordion type="single" collapsible>
                  <AccordionItem value="example-mcp-server-details" className="al-border-none">
                    <AccordionTrigger className="al-text-brand">
                      View mcp server details example
                    </AccordionTrigger>
                    <AccordionContent>
                      <CodeBlock
                        className="al-mb-0"
                        code={`{
  "mcp": {
    "inputs": [
      {
        "type": "promptString",
        "id": "github_token",
        "description": "GitHub Personal Access Token",
        "password": true
      }
    ],
    "servers": {
      "github": {
        "command": "npx",
        "args": [
          "-y",
          "@modelcontextprotocol/server-github"
        ],
        "env": {
          "GITHUB_PERSONAL_ACCESS_TOKEN": "<your_github_token>"
        }
      }
    }
  }
}`}
                        language="json"
                        theme="dark"
                        editorTheme="tomorrow"
                      />
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </li>
            </>
          )}
          <li>
            <div className="al-space-y-2">
              {isEditing ? (
                <Accordion type="single" collapsible>
                  <AccordionItem value="json-input-item" className="al-border-none">
                    <AccordionTrigger className="al-text-brand">
                      Update JSON Configuration
                    </AccordionTrigger>
                    <AccordionContent>
                      <Textarea
                        rows={20}
                        id="json-input"
                        value={jsonInput}
                        onChange={handleJsonChange}
                        placeholder="Paste JSON here..."
                      />
                      {jsonError && <div className="al-text-red-500 al-text-xs">{jsonError}</div>}
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              ) : (
                <>
                  <p>Copy the output from datapilot cli and paste it into the text area below.</p>
                  <Textarea
                    rows={20}
                    id="json-input"
                    value={jsonInput}
                    onChange={handleJsonChange}
                    placeholder="Paste JSON here..."
                  />
                  {jsonError && <div className="al-text-red-500 al-text-xs">{jsonError}</div>}
                </>
              )}
            </div>
          </li>
        </ul>
      </div>
    </div>
  );
};

export default McpConfigImporter;
